import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";

/**
 * `/sync/:id` — sync-job detail. Admin-only (server 403 ->
 * redirect to /my). Renders the job's timing, any error, a
 * dynamic counter grid built from whatever scalar fields the
 * `result` summary carries (works across the gitlab / clockify /
 * fakturoid sync shapes — historical `payout` rows still render
 * here for read-only browsing), logs, warnings, and the raw
 * payload + result JSON.
 */

const statusVariant: Record<
  string,
  "default" | "secondary" | "success" | "destructive" | "warning" | "outline"
> = {
  pending: "secondary",
  running: "default",
  completed: "success",
  failed: "destructive",
};

type Warning = {
  projectId: string;
  projectName: string;
  gitlabProjectId: string;
  message: string;
};

type Summary = {
  scope?: "single" | "all";
  warnings?: unknown;
  logs?: unknown;
  [key: string]: unknown;
};

type SyncJob = {
  id: string;
  type: string;
  status: "pending" | "running" | "completed" | "failed";
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  payload: unknown;
  result: unknown;
};

function isWarningArray(v: unknown): v is Warning[] {
  return Array.isArray(v) && v.every((w) => w && typeof w === "object" && "message" in w);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function asSummary(v: unknown): Summary | null {
  return v && typeof v === "object" ? (v as Summary) : null;
}

export const Route = createFileRoute("/_app/sync/$id")({
  loader: async ({ params }) => {
    try {
      return await api.get<SyncJob>(`/api/sync/${params.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: SyncDetailPage,
});

function SyncDetailPage() {
  const job = Route.useLoaderData();

  const summary = asSummary(job.result);
  const warnings = summary && isWarningArray(summary.warnings) ? summary.warnings : [];
  const logs = summary && isStringArray(summary.logs) ? summary.logs : [];
  const effectiveStatus =
    job.status === "completed" && warnings.length > 0 ? "warning" : job.status;

  const durationMs =
    job.startedAt && job.finishedAt
      ? new Date(job.finishedAt).getTime() - new Date(job.startedAt).getTime()
      : null;

  const counters =
    summary === null
      ? []
      : Object.entries(summary)
          .filter(
            ([k, v]) =>
              !["logs", "warnings", "scope"].includes(k) &&
              (typeof v === "number" || typeof v === "string"),
          )
          .map(([k, v]) => ({ key: k, value: v as string | number }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/sync" className="text-xs text-primary hover:underline">
            ← Back to sync jobs
          </Link>
          <h1 className="font-heading text-2xl font-bold mt-1">
            <span className="font-mono text-lg text-muted-foreground">{job.type}</span>{" "}
            <span className="text-muted-foreground">·</span>{" "}
            <span className="font-mono text-base text-muted-foreground">{job.id}</span>
          </h1>
        </div>
        <Badge variant={statusVariant[effectiveStatus] ?? "outline"}>
          {effectiveStatus === "warning"
            ? `completed (${warnings.length} warning${warnings.length === 1 ? "" : "s"})`
            : effectiveStatus}
        </Badge>
      </div>

      <Card>
        <CardContent className="p-5 space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Created</div>
              <LocalDate value={job.createdAt} withTime />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Started</div>
              {job.startedAt ? (
                <LocalDate value={job.startedAt} withTime />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Finished</div>
              {job.finishedAt ? (
                <LocalDate value={job.finishedAt} withTime />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          </div>
          {durationMs !== null && (
            <div className="text-xs text-muted-foreground">
              Duration: {(durationMs / 1000).toFixed(1)} s
            </div>
          )}
        </CardContent>
      </Card>

      {job.error && (
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-destructive mb-2">
              Error
            </h2>
            <pre className="rounded-md bg-destructive/10 text-destructive text-xs p-3 whitespace-pre-wrap break-words">
              {job.error}
            </pre>
          </CardContent>
        </Card>
      )}

      {counters.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Summary
            </h2>
            <div className="grid grid-cols-3 gap-3 text-sm">
              {counters.map((cnt) => (
                <div key={cnt.key}>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    {cnt.key.replace(/([A-Z])/g, " $1").trim()}
                  </div>
                  <div className="font-mono text-lg">{cnt.value}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {logs.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Logs ({logs.length})
            </h2>
            <pre className="rounded-md bg-muted/40 text-xs p-3 whitespace-pre-wrap break-words max-h-96 overflow-y-auto font-mono">
              {logs.join("\n")}
            </pre>
          </CardContent>
        </Card>
      )}

      {warnings.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-amber-600 mb-3">
              Warnings ({warnings.length})
            </h2>
            <ul className="space-y-2">
              {warnings.map((w, i) => (
                <li
                  key={`${w.projectId}-${i}`}
                  className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    {/* /projects/:id is still Next-owned. */}
                    <AppLink
                      href={`/projects/${w.projectId}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {w.projectName}
                    </AppLink>
                    <Badge variant="outline" className="font-mono text-[10px]">
                      gl:{w.gitlabProjectId}
                    </Badge>
                  </div>
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                    {w.message}
                  </pre>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-5">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Payload
          </h2>
          <pre className="rounded-md bg-muted/40 text-xs p-3 whitespace-pre-wrap break-words">
            {job.payload ? JSON.stringify(job.payload, null, 2) : "—"}
          </pre>
        </CardContent>
      </Card>

      {job.result !== null && (
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Result
            </h2>
            <pre className="rounded-md bg-muted/40 text-xs p-3 whitespace-pre-wrap break-words">
              {JSON.stringify(job.result, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
